/**
 * Puts a photograph of a frame onto a wall measured in inches.
 *
 * A masked rendering is a square photo with the frame somewhere inside it and a hole where
 * the artwork shows through, and it knows none of its own dimensions. What it does know,
 * because the server measured it, is the box the frame occupies in the photo and the box
 * the hole occupies, both in pixels.
 *
 * The wall reserves each frame a width in inches, so the frame's box in pixels over that
 * width in inches is the scale of the whole photograph. Everything else follows from it.
 */

// Artwork stopping exactly on the edge of the opening can leave a hairline of bare frame
// once a whole wall is scaled down to fit a screen. Overhang costs nothing, because the
// mask is drawn on top and is opaque everywhere except the hole.
export const BLEED = 0.01;

export function photographedFrameGeometry({ maskedRendering, widthInInches }) {
  const { exterior, opening, canvasSize, region } = maskedRendering;
  const pixelsPerInch = exterior.width / widthInInches;

  const openingWidth = opening.width / pixelsPerInch;
  const openingHeight = opening.height / pixelsPerInch;
  const artwork = {
    x: ((opening.x - exterior.x) / pixelsPerInch) - (openingWidth * BLEED),
    y: ((opening.y - exterior.y) / pixelsPerInch) - (openingHeight * BLEED),
    width: openingWidth * (1 + (BLEED * 2)),
    height: openingHeight * (1 + (BLEED * 2))
  };

  return {
    pixelsPerInch,
    // Negative, because the frame's own corner has to land on the origin: that is the
    // point the wall translates to the frame's position.
    mask: {
      x: -exterior.x / pixelsPerInch,
      y: -exterior.y / pixelsPerInch,
      size: canvasSize / pixelsPerInch
    },
    opening: artwork,
    // A photo of a whole set holds the neighbouring frames too, so a frame drawn from one
    // only draws its own slice of it
    region: region
      ? {
        x: (region.x - exterior.x) / pixelsPerInch,
        y: (region.y - exterior.y) / pixelsPerInch,
        width: region.width / pixelsPerInch,
        height: region.height / pixelsPerInch
      }
      : null,
    // Artwork in an oval opening is still a rectangle, and a thin round frame is not wide
    // enough to cover that rectangle's corners, so they would poke out past the frame.
    clip: maskedRendering.shape === 'ellipse'
      ? {
        cx: artwork.x + (artwork.width / 2),
        cy: artwork.y + (artwork.height / 2),
        rx: artwork.width / 2,
        ry: artwork.height / 2
      }
      : null
  };
}

/**
 * The outside size of a frame drawn from a photo of its whole set, in inches.
 *
 * A set photo sets its frames' sizes, read off the photo at the scale its openings give,
 * so anything laying out a bundle takes the frame's size from here rather than from its
 * frame spec. Anything else has no size of its own here and keeps the frame spec's.
 */
export function photographedSize(frameSpec) {
  const size = frameSpec && frameSpec.maskedRendering && frameSpec.maskedRendering.sizeInInches;

  return size && size.width && size.height ? size : null;
}

export default photographedFrameGeometry;
